<template>
  <div class="v-modal">
    <div class="v-modal__header">
      <span v-if="state == 'list'">{{ commodityTextListHeader }}</span>
      <span v-else-if="state == 'add'">Add Raw Material</span>
      <span v-else-if="state == 'modify'">Modify Raw Material</span>
      <span v-else-if="state == 'remove'">Remove Raw Material</span>
      <div @click="close()" class="close"></div>
    </div>
    <div class="v-modal__body">
      <Errors @dismiss="errors = []" :errors="errors"/>
      <template v-if="state == 'list'">
        <p v-if="scenarioCountryCrops.length == 1">
          {{ commodityTextList }} <b>{{ assessment.scenario.name }}</b> contains 1 raw material.
        </p>
        <p v-else>
          {{ commodityTextList }} <b>{{ assessment.scenario.name }}</b> contains {{ scenarioCountryCrops.length }} raw materials.
        </p>
        <table class="table table-striped">
          <thead>
            <tr>
              <th>Crop or Material</th>
              <th>Country of Origin</th>
              <th>Quantity</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="scenarioCountryCrop in scenarioCountryCrops" :key="scenarioCountryCrop.countryId + '|' + scenarioCountryCrop.cropId">
              <td>{{getCrop(scenarioCountryCrop.cropId).name}}</td>
              <td>{{getCountry(scenarioCountryCrop.countryId).name}}</td>
              <td>
                {{scenarioCountryCrop.quantity}}
                {{massUnits.find(unit => unit.unitId == scenarioCountryCrop.unitId).short}}
                per {{intervals.find(interval => interval.value === scenarioCountryCrop.intervalId).text}}
              </td>
              <td>
                <div class="d-flex justify-content-end gap-2">
                  <button @click="setScenarioCountryCrop('modify', reduceForInternal(scenarioCountryCrop))" title="Modify this raw material" class="btn btn-outline-primary btn-sm"><i class="fa-solid fa-pen"></i></button>
                  <button @click="setScenarioCountryCrop('remove', reduceForInternal(scenarioCountryCrop))" title="Remove this raw material" class="btn btn-outline-danger btn-sm"><i class="fa-solid fa-trash"></i></button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </template>
      <template v-else-if="(state == 'add') || (state == 'modify')">
        <template v-if="state == 'add'">
          <p>
            Add a raw material to {{ commodityTextAddRawMaterial }} <b>{{ (tempScenario || assessment.scenario).name }}</b>.
          </p>
          <div class="row mb-3">
            <label class="col-3 col-form-label">Crop or Material</label>
            <div class="col-4">
              <button @click="onOpenCropModal" class="btn btn-outline-secondary w-100 text-start" :class="{'color-inherit': selectedCrop}">{{selectedCrop ? selectedCrop.name : "Select Crop or Material"}}</button>
            </div>
          </div>
          <div v-if="selectedCrop" class="row mb-3">
            <label class="col-3 col-form-label">Country of Origin</label>
            <div class="col-4" :class="{'color-inherit': !!selectedCountries.length}">
              <multiselect
                :value="selectedCountries"
                :options="countries"
                :placeholder="!!selectedCountries.length ? selectedCountries[0].name : 'Search country'"
                label="name"
                track-by="countryID"
                @select="countrySelectHandler"
              ></multiselect>
            </div>
          </div>
        </template>
        <template v-if="!!selectedCountries.length">
          <p v-if="loadingCrops">
            Loading default water footprints for {{ selectedCrop.name.toLowerCase() }}...
          </p>
          <p v-else-if="specificWFt == 2">
            Country averages are not available for <b>{{ selectedCrop.name.toLowerCase() }}</b> from <b>{{ selectedCountries[0].name }}</b>. You have to specify your own green, blue and grey water footprints. Also, the water footprint of your crop cannot be spatially distributed across the country.
          </p>
          <template v-else>
            <p v-if="geographic">
              By default, the water footprint of your <b>{{ selectedCrop.name.toLowerCase() }}</b> from <b>{{ selectedCountries[0].name }}</b> will be calculated with the green, blue and grey country averages, but you can override them with more specific figures. In both cases, the water footprint will be spatially distributed across the country.
            </p>
            <p v-else>
              By default, the water footprint of your <b>{{ selectedCrop.name.toLowerCase() }}</b> from <b>{{ selectedCountries[0].name }}</b> will be calculated with green, blue and grey global averages, and you can override them with more specific figures. However, the water footprint of your material cannot be spatially distributed across the country.
            </p>
            <div class="form-check">
              <input type="radio" @click="setWFtToDefault()" v-model="specificWFt" :value="false" id="specificWFtFalse" class="form-check-input">
              <label for="specificWFtFalse" class="form-check-label">Use the default green, blue and grey averages</label>
            </div>
            <div class="form-check mb-3">
              <input type="radio" v-model="specificWFt" :value="true" id="specificWFtTrue" class="form-check-input">
              <label for="specificWFtTrue" class="form-check-label">Specify my own water footprints</label>
            </div>
          </template>
          <template v-if="!loadingCrops && specificWFt">
            <div class="row align-items-end mb-3">
              <label for="green" class="col-3 col-form-label">Green WF</label>
              <div class="col-5">
                <div class="input-group">
                  <input type="number" v-model.number="scenarioCountryCrop.greenWFt" id="green" placeholder="0" class="form-control">
                  <span class="input-group-text">m<sup>3</sup>/t</span>
                </div>
              </div>
            </div>
            <div class="row align-items-end mb-3">
              <label for="blue" class="col-3 form-label">Blue WF</label>
              <div class="col-5">
                <div class="input-group">
                  <input type="number" v-model.number="scenarioCountryCrop.blueWFt" id="blue" placeholder="0" class="form-control">
                  <span class="input-group-text">m<sup>3</sup>/t</span>
                </div>
              </div>
            </div>
            <div class="row align-items-end mb-3">
              <label for="grey" class="col-3 form-label">Grey WF</label>
              <div class="col-5">
                <div class="input-group">
                  <input type="number" v-model.number="scenarioCountryCrop.greyWFt" id="grey" placeholder="0" class="form-control">
                  <span class="input-group-text">m<sup>3</sup>/t</span>
                </div>
              </div>
            </div>
          </template>
        </template>
        <div class="row align-items-end mb-2">
          <label for="quantity" class="col-3 col-form-label">Quantity</label>
          <div class="col-3">
            <input type="number" v-model.number="scenarioCountryCrop.quantity" id="quantity" placeholder="0" class="form-control">
          </div>
          <div class="col-3">
            <label class="form-label">Unit</label>
            <select v-model="scenarioCountryCrop.unitId" class="form-select">
              <option v-for="unit in massUnits" :key="unit.unitId" :value="unit.unitId">
                {{ unit.long }}
              </option>
            </select>
          </div>
          <div class="col-2">
            <label class="form-label">Per</label>
            <select v-model="scenarioCountryCrop.intervalId" class="form-select">
              <option v-for="interval in intervals" :key="interval.value" :value="interval.value">
                {{ interval.text }}
              </option>
            </select>
          </div>
        </div>
      </template>
      <p v-else-if="state == 'remove'" class="text-center">
        <br/><br/>
        Are you sure you want to remove <b>{{ selectedCrop.name.toLowerCase() }}</b> from <b>{{ selectedCountries[0].name }}</b><br/><br/>
        from {{ commodityTextAddRawMaterial }} <b>{{ (tempScenario || assessment.scenario).name }}</b>?
      </p>
      <template v-if="loading">
        <transition name="fade">
          <div class="lottie">
            <Lottie></Lottie>
          </div>
        </transition>
      </template>
    </div>
    <div v-if="state == 'list'" class="v-modal__footer gap-2">
      <button @click="createScenarioCountryCrop()" class="btn btn-secondary">
        <span class="fa fa-plus"></span> Add raw material
      </button>
      <button @click="close()" class="btn btn-primary">Close</button>
    </div>
    <div v-else-if="tempScenario" class="v-modal__footer">
      <button @click="next()" class="btn btn-primary">Next</button>
    </div>
    <div v-else-if="assessment.scenario.type == 10" class="v-modal__footer">
      <button @click="saveScenarioCountryCrop()" class="btn btn-primary">Save</button>
    </div>
    <div v-else class="v-modal__footer justify-content-between">
      <button @click="setScenarioCountryCrop('list', null)" class="btn btn-primary">
        <span class="fa fa-chevron-left"></span> Cancel
      </button>
      <button v-if="(state == 'add') || (state == 'modify')" @click="saveScenarioCountryCrop()" class="btn btn-primary">Save</button>
      <button v-else @click="deleteScenarioCountryCrop()" class="btn btn-primary">Delete</button>
    </div>
  </div>
</template>

<script>
  import Multiselect from "vue-multiselect";
  import {assessment} from "@/services/assessment-model.js";
  import {
    countries,
    getCountry,
    getCrop,
    intervals,
    massUnits,
    scenarioCountryCrops,
    scenarioCountryCropsCreator,
    scenarioCountryCropsRemover,
    scenarios,
    scenariosLoader,
  } from "@/services/entity-model.js";
  import { closeModal, modalTypes, openModal } from "@/services/modal-model";
  import Errors from "@/components/shared/Errors.vue"
  import Lottie from "@/components/shared/Lottie.vue";
  import { EntityLoader, serverUrl } from "@/services/entity-model";

  export default {
    name: "ScenarioCountryCrop",
    components: {
      Multiselect,
      Errors,
      Lottie,
    },
    props: {
      modal: {type: Object},
      tempScenario: {type: Object}
    },
    data() {
      return {
        intervals,
        massUnits,
        countries,
        errors: [],
        assessment,
        scenarios,
        scenarioCountryCrops,
        scenariosLoader,
        scenarioCountryCropsCreator,
        scenarioCountryCropsRemover,
        countryCropsLoader: null,
        worldCropsLoader: null,
        state: "list",
        scenarioCountryCrop: null,
        geographic: true,
        specificWFt: false
      };
    },
    computed: {
      loading() {
        return this.scenariosLoader.status.loading || 
               this.scenarioCountryCropsCreator.status.loading || 
               this.scenarioCountryCropsRemover.status.loading;
      },
      loadingCrops() {
        return this.countryCropsLoader.status.loading || 
               this.worldCropsLoader.status.loading;
      },
      commodityTextListHeader() {
        if (this.assessment.assessmentType.name == "commodity") {
          return "Raw Materials";
        }
        return "Supply Chain";
      },
      commodityTextList() {
        if (this.assessment.assessmentType.name == "commodity") {
          return "Your commodity";
        }
        return "The supply chain of your business";
      },
      commodityTextAddRawMaterial() {
        if (this.assessment.assessmentType.name == "commodity") {
          return "your commodity";
        }
        return "the supply chain of your business";
      },
      commodityTextError() {
        if (this.assessment.assessmentType.name == "commodity") {
          return "A commodity";
        }
        return "A supply chain";
      },
      selectedCrop() {
        if (this.scenarioCountryCrop.cropId == null) {
          return null;
        }
        const crop = this.getCrop(this.scenarioCountryCrop.cropId);
        if ((crop.level < 4) && (crop.cropID != 1009)) {
          return null;
        }
        return crop;
      },
      selectedCountries() {
        if (this.scenarioCountryCrop.countryId == null) {
          return [];
        }
        return [this.getCountry(this.scenarioCountryCrop.countryId)];
      }
    },
    created() {
      if ((this.tempScenario != null) || (this.assessment.scenario.type == 10)) {
        this.createScenarioCountryCrop();
      }

      this.countryCrops = [];
      this.countryCropsLoader = new EntityLoader(this.countryCrops, "countryCrops",
        cropId => `${serverUrl}nessie/entities/country-crops.jsp?format=json&cors=true&crop=${cropId}`
      );
      this.worldCrops = [];
      this.worldCropsLoader = new EntityLoader(this.worldCrops, "worldCrops",
        cropId => `${serverUrl}nessie/rest/assessment/world-crops/${cropId}`,
        true
      );
    },
    methods: {
      close() {
        closeModal(this.modal);
      },
      getCrop,
      getCountry,
      onOpenCropModal() {
        openModal(modalTypes.crop, {
          tempCrop: getCrop(this.scenarioCountryCrop.cropId),
          validate: crop => {
            if (!crop || ((crop.level < 4) && (crop.cropID != 1009))) {
              return "No specific crop or material selected.";
            }
            return null;
          },
          commit: crop => {
            this.scenarioCountryCrop.cropId = crop.cropID;
            this.errors = [];

            if (crop.cropID < 121001) {
              this.countryCropsLoader.load(crop.cropID, () => this.setDefaultWFt());
            } else {
              this.worldCropsLoader.load(crop.cropID, () => this.setDefaultWFt());
            }
          },/*
        }, { 
          draggable: true, clickToClose: false,*/
          width: 650/*, 
          height: "auto" 
        */});
      },
      countrySelectHandler(eventVal) {
        this.scenarioCountryCrop.countryId = eventVal.countryID;

        this.setDefaultWFt();
      },
      reduceForInternal(scenarioCountryCrop) {
        const {countryId, cropId, intervalId, unitId, quantity, defaultGreenWFt, defaultBlueWFt, defaultGreyWFt, greenWFt, blueWFt, greyWFt} = scenarioCountryCrop;
        return {countryId, cropId, intervalId, unitId, quantity, defaultGreenWFt, defaultBlueWFt, defaultGreyWFt, greenWFt, blueWFt, greyWFt};
      },
      reduceForExternal(scenarioCountryCrop) {
        const {countryId, cropId, intervalId, unitId, quantity, greenWFt, blueWFt, greyWFt} = scenarioCountryCrop;
        return {countryId, cropId, intervalId, unitId, quantity, greenWFt, blueWFt, greyWFt};
      },
      setScenarioCountryCrop(state, scenarioCountryCrop) {
        this.countryCropsLoader.abort();
        this.worldCropsLoader.abort();

        this.errors = [];
        this.state = state;
        this.scenarioCountryCrop = scenarioCountryCrop;

        if (state == "modify") {
          this.setSpecificWFt();
        }
      },
      createScenarioCountryCrop() {
        this.errors = [];
        this.state = "add";
        this.scenarioCountryCrop = {
          countryId: null,
          cropId: null,
          intervalId: this.intervals[0].value,
          unitId: this.massUnits[0].unitId,
          quantity: null,
          defaultGreenWFt: null,
          defaultBlueWFt: null,
          defaultGreyWFt: null,
          greenWFt: null,
          blueWFt: null,
          greyWFt: null
        };
      },
      setDefaultWFt() {
        if (this.scenarioCountryCrop?.countryId == null) {
          return;
        }

        if (this.scenarioCountryCrop.cropId < 121001) {
          const c = this.countryCrops.find(countryCrop => countryCrop.countryID == this.scenarioCountryCrop.countryId);
          if (c != null) {
            this.scenarioCountryCrop.defaultGreenWFt = c.greenWFt;
            this.scenarioCountryCrop.defaultBlueWFt = c.blueWFt;
            this.scenarioCountryCrop.defaultGreyWFt = c.greyWFt;
          } else {
            this.scenarioCountryCrop.defaultGreenWFt = null;
            this.scenarioCountryCrop.defaultBlueWFt = null;
            this.scenarioCountryCrop.defaultGreyWFt = null;
          }
        } else {
          const w = this.worldCrops[0];
          this.scenarioCountryCrop.defaultGreenWFt = w.greenWFt;
          this.scenarioCountryCrop.defaultBlueWFt = w.blueWFt;
          this.scenarioCountryCrop.defaultGreyWFt = w.greyWFt;
        }

        this.setWFtToDefault();
        this.setSpecificWFt();
      },
      setWFtToDefault() {
        this.scenarioCountryCrop.greenWFt = this.scenarioCountryCrop.defaultGreenWFt ?? 0;
        this.scenarioCountryCrop.blueWFt = this.scenarioCountryCrop.defaultBlueWFt ?? 0;
        this.scenarioCountryCrop.greyWFt = this.scenarioCountryCrop.defaultGreyWFt ?? 0;
      },
      setSpecificWFt() {
        if (this.scenarioCountryCrop.cropId < 121001) {
          if (this.scenarioCountryCrop.defaultGreenWFt != null) { // If one default WF/t has a value, all WF/t have a value.
            this.geographic = true;
            this.specificWFt =
              this.scenarioCountryCrop.greenWFt !== (this.scenarioCountryCrop.defaultGreenWFt ?? 0) ||
              this.scenarioCountryCrop.blueWFt !== (this.scenarioCountryCrop.defaultBlueWFt ?? 0) ||
              this.scenarioCountryCrop.greyWFt !== (this.scenarioCountryCrop.defaultGreyWFt ?? 0);
          } else {
            this.geographic = false;
            this.specificWFt = 2;
          }
        } else {
          this.geographic = false;
          this.specificWFt =
            this.scenarioCountryCrop.greenWFt !== (this.scenarioCountryCrop.defaultGreenWFt ?? 0) ||
            this.scenarioCountryCrop.blueWFt !== (this.scenarioCountryCrop.defaultBlueWFt ?? 0) ||
            this.scenarioCountryCrop.greyWFt !== (this.scenarioCountryCrop.defaultGreyWFt ?? 0);
        }
      },
      saveScenarioCountryCrop() {
        this.validate();
        if (this.errors.length == 0) {
          const save = () => {
            this.scenarioCountryCropsCreator.load(this.assessment.scenario.code, ...Object.values(this.reduceForExternal(this.scenarioCountryCrop)), () => this.assessment.loadScenarioDerivedEntities());
            this.setScenarioCountryCrop("list", null);
          }
          if (this.assessment.scenario.type == 10) {
            const scenario = this.assessment.scenario;
            this.scenariosLoader.load(scenario.code, scenario.name, 11, scenario.unitID, scenario.quantity, scenarios => {
              this.assessment.setScenario(scenarios[0], false);
              save();
            });
          } else {
            save();
          }
        }
      },
      deleteScenarioCountryCrop() {
        this.validateDelete();
        if (this.errors.length == 0) {
          this.scenarioCountryCropsRemover.load(this.assessment.scenario.code, this.scenarioCountryCrop.countryId, this.scenarioCountryCrop.cropId, () => {
            this.setScenarioCountryCrop("list", null);
            this.assessment.loadScenarioDerivedEntities();
          });
        }
      },
      next() {
        this.tempScenario.countryCrops = [this.reduceForExternal(this.scenarioCountryCrop)];

        this.validate();
        if (this.errors.length == 0) {
          this.close();
          openModal(modalTypes.scenario1, { tempScenario: this.tempScenario, width: 700/*, height: "auto", draggable: false, scrollable: true*/ });
        }
      },
      validate() {
        this.errors = [];
        const checkNew = this.state != "modify";
        if (checkNew && (this.scenarioCountryCrop.cropId == null)) {
          this.errors.push("No crop or material selected.");
        } else if (checkNew && (this.scenarioCountryCrop.countryId == null)) {
          this.errors.push("No country of origin selected.");
        } else if (checkNew && this.scenarioCountryCrops.some(x => (x.cropId == this.scenarioCountryCrop.cropId) && (x.countryId == this.scenarioCountryCrop.countryId))) {
          this.errors.push("The selected crop and country combination already exists.")
        } else {
          if ((this.scenarioCountryCrop.greenWFt === null) || (this.scenarioCountryCrop.greenWFt === "") || (this.scenarioCountryCrop.greenWFt < 0)) {
            this.errors.push("Green water footprint must be greater than or equal to 0 m3/t.");
          }
          if ((this.scenarioCountryCrop.blueWFt === null) || (this.scenarioCountryCrop.blueWFt === "") || (this.scenarioCountryCrop.blueWFt < 0)) {
            this.errors.push("Blue water footprint must be greater than or equal to 0 m3/t.");
          }
          if ((this.scenarioCountryCrop.greyWFt === null) || (this.scenarioCountryCrop.greyWFt === "") || (this.scenarioCountryCrop.greyWFt < 0)) {
            this.errors.push("Grey water footprint must be greater than or equal to 0 m3/t.");
          }
          if (this.scenarioCountryCrop.greenWFt + this.scenarioCountryCrop.blueWFt + this.scenarioCountryCrop.greyWFt == 0) {
            this.errors.push("At least one of the green, blue or grey water footprints must be greater than 0 m3/t.");
          }
        }
        if (this.scenarioCountryCrop.quantity <= 0) {
          this.errors.push("Quantity must be greater than 0.");
        }
      },
      validateDelete() {
        this.errors = [];
        if (this.scenarioCountryCrops.length == 1) {
          this.errors.push(this.commodityTextError + " needs at least 1 raw material.");
        }
      }
    }
  }
</script>

<style lang="scss">
.btn-outline-secondary.color-inherit,
.col-4.color-inherit .multiselect__placeholder {
  color: inherit;
}
</style>
